import { Lock, PauseCircle } from 'lucide-react';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { ENTITY_STATUS_ERROR_CODES } from '@/lib/constants/entity-status-errors';
import { getApiErrorCode } from '@/lib/utils/error.utils';

export type EntityBlockedVariant = 'suspended' | 'deleted';

export interface EntityAccessBlockedStateProps {
  variant: EntityBlockedVariant;
}

const S = UI_STRINGS.ENTITY_BLOCKED;

/**
 * One lookup from the Backend's error code to which blocked variant to show,
 * rather than branching on the code at each call site (mnr-standards:
 * no-repeated-ternary-on-one-discriminant). Entity-agnostic and reusable -
 * every startup-scoped query (the profile fetch in `ProfileEditor`, the team
 * fetch in `TeamPermissionsPage`, ...) keys off this one map rather than each
 * page maintaining its own copy.
 */
const BLOCKED_VARIANT_FOR_CODE: Record<string, EntityBlockedVariant> = {
  [ENTITY_STATUS_ERROR_CODES.SUSPENDED]: 'suspended',
  [ENTITY_STATUS_ERROR_CODES.DELETED]: 'deleted',
};

/**
 * The blocked variant for a query error, or `undefined` for any other
 * failure (a network error, an unrelated 500, ...) - callers must fall back
 * to their own generic error state in that case, never block on a code this
 * map doesn't recognize.
 */
export function blockedVariantForError(error: unknown): EntityBlockedVariant | undefined {
  return BLOCKED_VARIANT_FOR_CODE[getApiErrorCode(error) ?? ''];
}

/**
 * One config per variant, keyed by the discriminant rather than branched on
 * separately for icon/heading/body (mnr-standards: no-repeated-ternary-on-
 * one-discriminant).
 */
const VARIANT_CONFIG: Record<
  EntityBlockedVariant,
  { icon: typeof Lock; heading: string; body: string }
> = {
  suspended: { icon: PauseCircle, heading: S.SUSPENDED_TITLE, body: S.SUSPENDED_DESCRIPTION },
  deleted: { icon: Lock, heading: S.DELETED_TITLE, body: S.DELETED_DESCRIPTION },
};

/**
 * Full-page member-facing state for a startup an admin has suspended or
 * deleted (MYN-131 AC-4). Two distinct states, not one generic denial:
 * suspension reads as temporary and recoverable, deletion as permanent -
 * both carry contact-support instructions. Renders in place of the startup
 * profile route's normal content; it does not log the member out or redirect
 * to login - their session stays valid and every non-startup-scoped surface
 * (account, notifications, events, resources) remains reachable, so there is
 * no "Log out" action here, only a way back to the dashboard.
 */
export function EntityAccessBlockedState({ variant }: EntityAccessBlockedStateProps) {
  const { icon: Icon, heading, body } = VARIANT_CONFIG[variant];

  return (
    <LinkStatusCard
      icon={<Icon size={32} />}
      heading={heading}
      body={body}
      footnote={S.CONTACT_SUPPORT}
      ctaLabel={S.ACTION}
      ctaTo={ROUTES.DASHBOARD}
      fullScreen
    />
  );
}
